/** Mirrors API.Utils.RoleHierarchyUtils.DefaultOrderByShortName — kept in sync by RoleHierarchySyncTests */
const DEFAULT_ORDER_BY_SHORT_NAME = {
    SSA: 1,
    AD: 2,
    DAD: 7,
    PL: 4,
    PD: 5,
    BI: 6,
    RBI: 9,
    DO: 8,
    DL: 3,
};

export function isBotmarkSuperAdmin(authenticated) {
    return authenticated?.isAdmin === true || authenticated?.isAdmin === 'True';
}

export function getRoleHierarchyOrder(role) {
    if (!role) {
        return Number.MAX_SAFE_INTEGER;
    }

    const explicitOrder = role.hierarchyOrder;
    if (explicitOrder != null && explicitOrder > 0) {
        return explicitOrder;
    }

    const shortName = role.shortName;
    if (shortName && DEFAULT_ORDER_BY_SHORT_NAME[shortName] != null) {
        return DEFAULT_ORDER_BY_SHORT_NAME[shortName];
    }

    return Number.MAX_SAFE_INTEGER;
}

export function canAssignRole(actingRole, targetRole, isSuperAdmin) {
    if (isSuperAdmin) {
        return true;
    }

    if (!actingRole || !targetRole) {
        return false;
    }

    return getRoleHierarchyOrder(targetRole) >= getRoleHierarchyOrder(actingRole);
}

export function findRoleByName(allRoles, roleName) {
    if (!roleName || !allRoles?.length) {
        return null;
    }
    return allRoles.find((role) => role.name === roleName) ?? null;
}

export function canModifyUserRole(authenticated, allRoles, subjectRole) {
    if (isBotmarkSuperAdmin(authenticated)) {
        return true;
    }

    const actingRole = getActingRole(authenticated, allRoles);
    if (!actingRole || !subjectRole) {
        return false;
    }

    return canAssignRole(actingRole, subjectRole, false);
}

export function canModifyEmployeeByRoleName(authenticated, allRoles, employeeRoleName) {
    return canModifyUserRole(authenticated, allRoles, findRoleByName(allRoles, employeeRoleName));
}

export function sortRolesByHierarchy(allRoles) {
    return [...(allRoles ?? [])].sort((left, right) => {
        const leftOrder = getRoleHierarchyOrder(left);
        const rightOrder = getRoleHierarchyOrder(right);
        if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
        }
        const leftName = left.name ?? '';
        const rightName = right.name ?? '';
        return leftName.localeCompare(rightName);
    });
}

function normalizeRoleId(roleId) {
    return roleId ? String(roleId).toLowerCase() : null;
}

function getActingRole(authenticated, allRoles) {
    const primaryRoleId = authenticated?.roleIds?.[0];
    if (!primaryRoleId) {
        return null;
    }
    return allRoles.find((role) => normalizeRoleId(role.id) === normalizeRoleId(primaryRoleId)) ?? null;
}

export function getAssignableRoles(authenticated, allRoles) {
    const sortedRoles = sortRolesByHierarchy(allRoles);
    if (!sortedRoles.length) {
        return [];
    }

    if (isBotmarkSuperAdmin(authenticated)) {
        return sortedRoles;
    }

    const actingRole = getActingRole(authenticated, sortedRoles);
    if (!actingRole) {
        return [];
    }

    return sortedRoles.filter((role) => canAssignRole(actingRole, role, false));
}
